// Shared text formatting helpers.

// Tokens that should stay fully upper-cased when humanizing snake_case values.
const ACRONYMS = new Set(['rvn', 'ooh', 'cv', 'hmrc', 'gdpr', 'rcvs', 'vds', 'cpd']);

// Title-case each word of a snake_case/spaced string, preserving known acronyms
// (e.g. "rvn" -> "RVN", "ooh_veterinary_surgeon" -> "OOH Veterinary Surgeon").
export const capitalizeWords = (str) => {
  return (str || '')
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');
};

// Staff are shown by first name only — except the Claire / Clair / Clare
// (Claire Hopkins, Clair Loding, Clare Rogers), whose first names alone could be
// mixed up, so they keep their surname. Use for every staff/user name on screen.
const KEEP_SURNAME = new Set(['claire', 'clair', 'clare']);

export const staffName = (fullName) => {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return KEEP_SURNAME.has(parts[0].toLowerCase()) ? parts.join(' ') : parts[0];
};

// Server datetimes are stored in UTC. Postgres `timestamptz` columns (created_at)
// serialize WITH an offset, but the manually-set columns (sent/attempted/failed,
// contract/interview/verified dates — all written with datetime.utcnow()) are
// timezone-NAIVE and serialize without a marker. JS would then parse those as
// LOCAL time, showing them an hour behind during BST. Tag offset-less datetimes
// as UTC so toLocale* converts the true instant to the viewer's timezone (London).
// Pure calendar dates ("2026-12-17", no time component) are left as-is.
const parseServerDate = (value) => {
  if (!value) return null;
  let v = value;
  if (typeof v === 'string' && v.includes('T') && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(v)) {
    v = v + 'Z';
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

// Every date on screen is British (day/month/year) regardless of the viewer's
// browser language. Before 2026-10-08 the no-options path fell through to the
// browser locale, so a US-English browser showed start dates as mm/dd/yyyy.
const LOCALE = 'en-GB';

// Format a server datetime as a date + time (dd/mm/yyyy, hh:mm:ss). Empty string for null.
export const formatDateTime = (value) => {
  const d = parseServerDate(value);
  return d ? d.toLocaleString(LOCALE) : '';
};

// Format a server datetime/date as dd/mm/yyyy. Pass options for custom formatting.
export const formatDate = (value, options, locale = LOCALE) => {
  const d = parseServerDate(value);
  if (!d) return '';
  return d.toLocaleDateString(locale, options);
};

// A calendar date ("2026-12-17") spelled out: "Thursday 17 December 2026".
// Empty string for null or a partially typed value.
export const formatDateLong = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
  const d = new Date(value + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(LOCALE, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};
